import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useFormattedLocationString } from '@eventbrite/consumer-search.event-renderer';
import { fromZonedTime } from 'date-fns-tz';
import { useAppContext } from '@/components/_shared/context/AppContext';

import type { BadgeInfo } from '@/components/UrgencySignals/badgeInfo';
import { getHighestPriorityBadge } from '@/components/UrgencySignals/utils';
import {
  getDisplayTimezone,
  getFormattedDateOnlyLocaleBased,
  MonthFormat,
  WeekdayFormat,
  shouldShow12hCycle,
} from '@/utils/dateTime';
import { useAttendeeTimezone } from '@/hooks/useAttendeeTimezone';
import type { RelatedEvent, UrgencySignals, Venue } from '../../types';

// Type assertion helper for external library compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExternalVenue = any;

export interface FormattedEventData {
  id?: string;
  isFree: boolean;
  imageUrl: string;
  isOnlineEvent: boolean;
  isPromoted: boolean;
  locationString: string;
  formattedPriceString: string;
  name?: string;
  urgencySignals: UrgencySignals;
  url: string;
  venue: Venue | undefined;
  formattedEventDateTimeString: string;
  urgencySignalBadge: BadgeInfo | null;
  priceString?: string;
}

const formatOnlineEventLocation = (
  formatMessage: (descriptor: { id: string; defaultMessage?: string; description?: string }) => string,
): string => {
  return formatMessage({
    id: 'Online event',
    defaultMessage: 'Online event',
    description: 'Label for online event location',
  });
};

const formatInPersonEventLocation = (locationStringFromHook: string): string => {
  return locationStringFromHook;
};

const formatDateTimeString = (
  startDate: string | undefined,
  startTime: string | undefined,
  timezone: string,
  locale: string,
  isOnlineEvent: boolean,
  attendeeTimezone?: string,
): string => {
  if (!startDate) return '';

  const normalizedTime = startTime ? (startTime.length === 5 ? `${startTime}:00` : startTime) : '00:00:00';
  const dateObj = fromZonedTime(`${startDate}T${normalizedTime}`, timezone);

  const targetTimezone = getDisplayTimezone({
    isOnlineEvent,
    eventTimezone: timezone,
    attendeeTimezone,
  });

  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: shouldShow12hCycle(locale),
    timeZone: targetTimezone,
  });

  const dateWithWeekday = getFormattedDateOnlyLocaleBased(dateObj, {
    locale,
    timezone: targetTimezone,
    monthFormat: MonthFormat.LONG,
    showYear: false,
    weekday: WeekdayFormat.SHORT,
  });
  const time = timeFormatter.format(dateObj);

  // Construct final format: "Mon, May 5 • 11:30 AM" or "Lun, 5 Mayo • 11:30"
  return `${dateWithWeekday} • ${time}`;
};

const getUrgencySignalBadge = getHighestPriorityBadge;

const formatPriceString = (
  isFree: boolean,
  formattedPriceString: string | undefined,
  formatMessage: (descriptor: { id: string }, values?: Record<string, string>) => string,
): string | undefined => {
  if (!isFree && formattedPriceString) {
    return formatMessage({ id: 'Starts at {formattedMinPrice}' }, { formattedMinPrice: formattedPriceString });
  }
  return undefined;
};

const buildFormattedEventData = (
  relatedEvent: RelatedEvent,
  locationString: string,
  formattedEventDateTimeString: string,
  urgencySignalBadge: BadgeInfo | null,
  priceString: string | undefined,
): FormattedEventData => ({
  id: relatedEvent.id,
  isFree: relatedEvent.isFree ?? false,
  imageUrl: relatedEvent.imageUrl ?? '',
  isOnlineEvent: relatedEvent.isOnlineEvent ?? false,
  isPromoted: relatedEvent.isPromoted ?? false,
  locationString,
  formattedPriceString: relatedEvent.formattedPriceString ?? '',
  name: relatedEvent.name,
  urgencySignals: relatedEvent.urgencySignals ?? {},
  url: relatedEvent.url ?? '#',
  venue: relatedEvent.venue,
  formattedEventDateTimeString,
  urgencySignalBadge,
  priceString,
});

export const useFormattedEventData = (relatedEvent: RelatedEvent | null): FormattedEventData | null => {
  const { formatMessage } = useIntl();
  const attendeeTimezone = useAttendeeTimezone(relatedEvent?.isOnlineEvent ?? false);
  const { data } = useAppContext();
  const locale = useMemo(() => {
    return data.locale || navigator.language;
  }, [data.locale]);

  // External hooks - must be called unconditionally
  const locationStringFromHook = useFormattedLocationString({
    venue: relatedEvent?.venue as ExternalVenue,
    style: 'full',
  });

  const formattedData = useMemo((): FormattedEventData | null => {
    if (!relatedEvent) {
      return null;
    }

    const locationString = relatedEvent.isOnlineEvent
      ? formatOnlineEventLocation(formatMessage)
      : formatInPersonEventLocation(locationStringFromHook);

    const formattedEventDateTimeString = formatDateTimeString(
      relatedEvent.startDate,
      relatedEvent.startTime,
      relatedEvent.timezone ?? 'UTC',
      locale,
      relatedEvent.isOnlineEvent ?? false,
      attendeeTimezone,
    );

    const urgencySignalBadge = getUrgencySignalBadge(relatedEvent.urgencySignals);

    const priceString = formatPriceString(
      relatedEvent.isFree ?? false,
      relatedEvent.formattedPriceString,
      formatMessage,
    );

    return buildFormattedEventData(
      relatedEvent,
      locationString,
      formattedEventDateTimeString,
      urgencySignalBadge,
      priceString,
    );
  }, [relatedEvent, formatMessage, locationStringFromHook, attendeeTimezone, locale]);

  return formattedData;
};
